import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Database, Shield, Workflow, Code2, CheckCircle2, Rocket, Trophy, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminDevCaseStudySimulatorTab from "@/components/case-study/AdminDevCaseStudySimulatorTab";
import CaseStudySqlTasksTab from "@/components/case-study/CaseStudySqlTasksTab";
import CaseStudyMentorTab from "@/components/case-study/CaseStudyMentorTab";
import CaseStudyFinalReport from "@/components/case-study/CaseStudyFinalReport";
import RealOrgVerificationTab from "@/components/case-study/RealOrgVerificationTab";

const ARCH_ICONS = { database: Database, shield: Shield, workflow: Workflow, "code-2": Code2, "check-circle-2": CheckCircle2, rocket: Rocket };

const REAL_ORG_PROPS = {
  apiPrefix: "/salesforce", connectPath: "/admin-dev/connect", verifyPath: "/salesforce/verify-admin-dev",
  verifyLabels: { custom_objects: "Custom Objects", apex_classes: "Apex Classes", apex_triggers: "Apex Triggers" },
  heading: "Go Beyond the Simulator",
  description: "Connect your own real Salesforce org — even a free Developer Edition — and we'll verify, via read-only API calls, that you've actually built Custom Objects and Apex for real.",
  connectLabel: "Connect Your Org",
};

/** Admin + Dev-specific Case Study detail page. Forked from CaseStudyDetailPage.js
 * because the Admin + Dev simulator uses a 4-step engine (not the 7-step Data
 * Cloud pipeline), so `simulator_steps_completed` length checks and the
 * simulator tab component itself both need to differ. */
export default function AdminDevCaseStudyDetailPage() {
  const apiPrefix = "/admin-dev-case-studies";
  const { caseStudyId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [cs, setCs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [simDone, setSimDone] = useState(false);
  const [sqlDone, setSqlDone] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`${apiPrefix}/${caseStudyId}`).then(({ data }) => {
      setCs(data);
      setSimDone(Object.keys(data.simulator_steps_completed || {}).length === 4);
      setSqlDone(Object.keys(data.sql_tasks_completed || {}).length === (data.sql_tasks?.length || 3));
      if (data.status === "completed") setTab("report");
    }).catch(() => toast.error("Could not load this case study")).finally(() => setLoading(false));
  }, [caseStudyId]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`${apiPrefix}/${caseStudyId}/complete`);
      setCs((prev) => ({ ...prev, status: "completed", final_result: data }));
      setTab("report");
      refreshUser();
      toast.success("Case study complete!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't complete the case study — please try again.");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="admin-dev-cs-detail-loading"><div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!cs) return null;

  const isCompleted = cs.status === "completed";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-dev-cs-detail-page">
      <button onClick={() => navigate("/admin-dev/case-studies")} data-testid="back-to-admin-dev-case-studies-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Case Studies
      </button>

      <div className="glass-panel rounded-2xl p-6 mb-6">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">{cs.industry}</span>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white mt-2" data-testid="admin-dev-cs-title">{cs.title}</h1>
        <p className="text-sm text-slate-400 mt-2">{cs.tagline}</p>
        <div className="flex items-center gap-3 mt-4">
          <span className="text-xs font-semibold bg-white/5 border border-white/10 rounded-full px-3 py-1 text-slate-300">{cs.difficulty}</span>
          <span className="text-xs font-bold text-[#FBBF24]">+{cs.xp_reward} XP</span>
          {isCompleted && (
            <span className="flex items-center gap-1 text-xs font-bold text-emerald-400" data-testid="admin-dev-cs-completed-badge">
              <CheckCircle2 className="w-3.5 h-3.5" /> Completed · {cs.final_result.overall_score}%
            </span>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="admin-dev-cs-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="admin-dev-cs-tab-overview">Business Problem</TabsTrigger>
          <TabsTrigger value="simulator" data-testid="admin-dev-cs-tab-simulator">Simulator{simDone ? " \u2713" : ""}</TabsTrigger>
          <TabsTrigger value="sql" data-testid="admin-dev-cs-tab-sql">SOQL Tasks{sqlDone ? " \u2713" : ""}</TabsTrigger>
          <TabsTrigger value="mentor" data-testid="admin-dev-cs-tab-mentor">AI Mentor</TabsTrigger>
          <TabsTrigger value="real-org" data-testid="admin-dev-cs-tab-real-org">Real Org</TabsTrigger>
          <TabsTrigger value="report" data-testid="admin-dev-cs-tab-report" disabled={!isCompleted}>Final Report</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="admin-dev-cs-business-problem">
            <h3 className="font-heading font-semibold text-white text-sm mb-3">The Business Problem</h3>
            <p className="text-sm text-slate-300 leading-relaxed">{cs.business_problem}</p>
          </div>
          <div className="glass-panel rounded-2xl p-6" data-testid="admin-dev-cs-architecture">
            <h3 className="font-heading font-semibold text-white text-sm mb-5">Architecture Walkthrough</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {cs.architecture_steps.map((step, i) => {
                const Icon = ARCH_ICONS[step.icon] || Workflow;
                return (
                  <div key={i} className="flex items-start gap-3" data-testid={`admin-dev-cs-arch-step-${i}`}>
                    <div className="w-9 h-9 rounded-lg bg-amber-400/10 flex items-center justify-center shrink-0 text-amber-300 font-bold text-xs">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{i + 1}. {step.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="simulator" className="mt-6">
          <AdminDevCaseStudySimulatorTab caseStudyId={caseStudyId} scenario={cs.simulator} initialSteps={cs.simulator_steps_completed} onAllDone={setSimDone} disabled={isCompleted} apiPrefix={apiPrefix} />
        </TabsContent>

        <TabsContent value="sql" className="mt-6">
          <CaseStudySqlTasksTab caseStudyId={caseStudyId} tasks={cs.sql_tasks} initialResults={cs.sql_tasks_completed} onAllDone={setSqlDone} apiPrefix={apiPrefix} />
        </TabsContent>

        <TabsContent value="mentor" className="mt-6">
          <CaseStudyMentorTab caseStudyId={caseStudyId} company={cs.company} apiPrefix={apiPrefix} />
        </TabsContent>

        <TabsContent value="real-org" className="mt-6">
          <RealOrgVerificationTab {...REAL_ORG_PROPS} />
        </TabsContent>

        <TabsContent value="report" className="mt-6">
          <CaseStudyFinalReport result={cs.final_result} company={cs.company} />
        </TabsContent>
      </Tabs>

      {!isCompleted && (
        <div className="flex justify-end mt-6">
          <button
            onClick={handleComplete} disabled={!simDone || !sqlDone || completing} data-testid="admin-dev-cs-complete-btn"
            className="flex items-center gap-2 text-sm font-semibold bg-amber-500 hover:bg-amber-500/90 disabled:opacity-40 text-white px-5 py-2.5 rounded-lg"
          >
            {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />} Complete Case Study & Get Final Score
          </button>
        </div>
      )}
    </div>
  );
}
